import { isScreenshotMode } from "@constants/common";
import { tagIconUrlMap } from "@constants/mocked-videos";
import { isI18nKeyExists } from "@helpers/common";
import { randomizeDistribution } from "@helpers/mocked-helpers";
import { useAppStore } from "@hooks/useAppStore";
import type { SessionConfigFiltersType } from "@memorang/constants/config/schema";
import { titleCase } from "@memorang/helpers/common";
import { isWeb } from "@memorang/helpers/platform";
import type { BundleMetaData, Plan } from "@memorang/types";
import type { Distribution } from "@memorang/types";
import type { Item, MixedItemTag, TagsByType } from "@memorang/types";
import {
	collectAncestorIds,
	collectDescendantIds,
	findTagById,
	findTopLevelAncestorType,
	flattenTagsToDepth,
	hasRenderableSubtree,
	subtreeContainsId,
} from "@memorang/ui/helpers/tag-tree";
import { updateUserProperties } from "analytics";
import i18next, { t } from "i18next";
import { DateTime } from "luxon";
import pluralize from "pluralize";
import type { CurrentView } from "./types";

const customTitleMap: Record<string, string> = {
	"Major Area": "Knowledge Levels",
	"Diagnostic Level": "Reasoning",
};

const isItemArray = (arr: (MixedItemTag | Item)[]): arr is Item[] => {
	return (
		arr.length > 0 &&
		"distribution" in arr[0] &&
		typeof arr[0].distribution === "string"
	);
};

const calculateTagDist = (
	tags: (MixedItemTag | Item)[],
	filterItems: SessionConfigFiltersType,
	showPointsBasedScore?: boolean,
): Distribution => {
	if (isItemArray(tags) && !showPointsBasedScore) {
		return tags.reduce(
			(a, { distribution }) => {
				a[distribution] = a[distribution] ? a[distribution] + 1 : 1;
				return a;
			},
			{
				...Object.assign(
					filterItems.reduce<Distribution>((acc, level) => {
						acc[level.distKey] = 0;
						return acc;
					}, {}),
					{ none: 0 },
				),
			},
		);
	}

	return tags.reduce(
		(acc, tag) => {
			Object.entries(tag.distribution).reduce((_, [key, value]) => {
				acc[key] += value;
				return acc;
			}, acc);
			return acc;
		},
		{
			...Object.assign(
				filterItems.reduce<Distribution>((acc, level) => {
					acc[level.distKey] = 0;
					return acc;
				}, {}),
				{ none: 0 },
			),
		},
	);
};
const getItemsCountDescription = (count: number) =>
	count ? t("common.itemCount", { count }) : "";
const filterTagsByType = (tagsByType: TagsByType, currentView: CurrentView) => {
	return Object.keys(tagsByType).reduce((a: TagsByType, c) => {
		const currentTags = tagsByType[c];
		const currentTagsWithDist = currentTags.filter(
			(item: MixedItemTag) =>
				item.displayType?.includes(currentView) &&
				(item.numItems || 0 || item.items?.length || 0) > 0,
		);
		a[c] = currentTagsWithDist;
		return a;
	}, {});
};

const filterTagsByTypeDeep = (
	tagsByType: TagsByType,
	currentView: CurrentView,
): TagsByType => {
	return Object.keys(tagsByType).reduce((acc: TagsByType, key) => {
		acc[key] = tagsByType[key].filter((tag) =>
			hasRenderableSubtree(tag, currentView),
		);
		return acc;
	}, {});
};

export const getAllItemsFromTags = (tags: MixedItemTag[]): Item[] => {
	if (!tags?.length) {
		return [];
	}

	return tags.reduce<Item[]>((items, tag) => {
		const tagItems = getAllItemsFromTag(tag);
		for (const item of tagItems) {
			items.push(item);
		}
		return items;
	}, []);
};
const getAllItemsFromTag = (tag: MixedItemTag): Item[] => {
	let allItems: Item[] = [];

	if (tag.items && tag.items.length > 0) {
		allItems = [...allItems, ...tag.items];
	}

	if (tag.children && tag.children.length > 0) {
		allItems = [...allItems, ...getAllItemsFromTags(tag.children)];
	}

	return allItems;
};

const getDisplayTitle = (tag: MixedItemTag): string => {
	const tagLabel = tag.title || tag.label || "";
	return tag.code ? `${tag.code} - ${tagLabel}` : tagLabel;
};

const computeLocked = (
	tagId: number,
	freeTagIds: number[],
	hasAccess?: boolean,
	parentUnlocked?: boolean,
): boolean => {
	if (hasAccess || parentUnlocked) {
		return false;
	}
	return !freeTagIds?.includes(tagId);
};

const transformTagDeep = (
	tag: MixedItemTag,
	depth: number,
	maxDepth: number,
	freeTagIds: number[],
	hasAccess?: boolean,
	includeCount?: boolean,
	parentUnlocked = false,
): MixedItemTag => {
	const dist = tag.distribution;
	const locked = computeLocked(tag.id, freeTagIds, hasAccess, parentUnlocked);

	const transformedChildren =
		depth < maxDepth && tag.children?.length
			? tag.children.map((child) =>
					transformTagDeep(
						child,
						depth + 1,
						maxDepth,
						freeTagIds,
						hasAccess,
						includeCount,
						!locked,
					),
				)
			: undefined;

	const childItems: Item[] = transformedChildren
		? transformedChildren.flatMap((child) => child.items ?? [])
		: tag.children?.length
			? getAllItemsFromTags(tag.children)
			: [];
	const aggregatedItems: Item[] = [...(tag.items ?? []), ...childItems];
	const computedNumItems = tag.numItems || aggregatedItems.length || 0;

	return {
		...tag,
		iconUrl: tagIconUrlMap[tag.title || tag.label || ""],
		locked,
		distribution: isScreenshotMode ? randomizeDistribution(dist) : dist,
		numItems: computedNumItems,
		items: aggregatedItems,
		description: getItemsCountDescription(computedNumItems),
		count: computedNumItems,
		type: tag.type,
		title: getDisplayTitle(tag),
		score: Math.round(tag.averageScore || 0),
		children: transformedChildren,
	} as MixedItemTag;
};

const groupTagsByType = (
	tags: MixedItemTag[] | undefined,
	currentView: CurrentView,
	freeTagIds: number[] = [],
	hasAccess?: boolean,
	includeCount?: boolean,
	maxDepth = 1,
) => {
	const normalizedTags = tags || [];
	const tagsByType = normalizedTags.reduce((acc: TagsByType, tag) => {
		const tagType = customTitleMap[tag.type] || tag.type;
		if (!acc[tagType]) {
			acc[tagType] = [];
		}
		acc[tagType].push(
			transformTagDeep(tag, 1, maxDepth, freeTagIds, hasAccess, includeCount),
		);
		return acc;
	}, {});

	const filteredTagsByType =
		maxDepth > 1
			? filterTagsByTypeDeep(tagsByType, currentView)
			: filterTagsByType(tagsByType, currentView);

	return filteredTagsByType;
};

const hasProductAccess = (
	productMetadata?: BundleMetaData,
	productNumericId?: number,
) => {
	if (productMetadata) {
		const {
			subscription: { status, endDate },
		} = productMetadata;
		const isSubExpired = endDate
			? DateTime.now() > DateTime.fromISO(endDate)
			: true;
		const hasAccess = status === "ACTIVE" && !isSubExpired;
		if (productNumericId && hasAccess) {
			updateUserProperties({
				premium_user: "true",
				products: JSON.stringify([
					{
						id: productNumericId,
						type: "study_pack",
					},
				]),
			});
		}
		return isScreenshotMode ? true : hasAccess;
	}
	return false;
};

const filterPlans = (plans: Plan[]) => {
	return plans.filter(
		(plan) =>
			(isWeb && plan.isMobile === false) ||
			(!isWeb && plan.isMobile === true) ||
			plan.isMobile == null,
	);
};

const getNumAttempted = (distribution: Distribution) => {
	return Object.entries(distribution).reduce((acc, [key, value]) => {
		if (key !== "none") {
			return acc + value;
		}
		return acc;
	}, 0);
};

const getDisplayTagType = (
	tags: MixedItemTag[] | undefined,
): string | undefined =>
	tags?.find((tag) => tag.displayType?.includes("Display"))?.type;

const pluralizeLabel = (label: string, count?: number): string => {
	if (i18next.language !== "en-US") {
		return label;
	}
	return count == null ? pluralize(label) : pluralize(label, count);
};

const getTopicsSectionLabel = (
	tags: MixedItemTag[] | undefined,
	count?: number,
): string => {
	const masterySubheaderLabel =
		useAppStore.getState().homeView?.masterySubheaderLabel;
	if (masterySubheaderLabel) {
		// Partner-authored copy is used verbatim unless a count needs agreement.
		return count == null
			? masterySubheaderLabel
			: pluralizeLabel(masterySubheaderLabel, count);
	}

	const tagType = getDisplayTagType(tags);
	if (!tagType) {
		return pluralizeLabel("Topics", count);
	}

	const mappedType = customTitleMap[tagType] || tagType;
	const sessionConfigKey = `sessionConfig.${mappedType}`;
	if (isI18nKeyExists(sessionConfigKey)) {
		return pluralizeLabel(t(sessionConfigKey), count);
	}

	return titleCase(pluralizeLabel(mappedType, count));
};

const getTitleForType = (type: string, count?: number): string => {
	const mappedType = customTitleMap[type] || type;
	const sessionConfigKey = `sessionConfig.${mappedType}`;
	if (isI18nKeyExists(sessionConfigKey)) {
		return pluralizeLabel(t(sessionConfigKey), count);
	}
	return titleCase(pluralizeLabel(mappedType, count));
};

export {
	getItemsCountDescription,
	groupTagsByType,
	hasProductAccess,
	calculateTagDist,
	filterPlans,
	getNumAttempted,
	getTopicsSectionLabel,
	getTitleForType,
	flattenTagsToDepth,
	findTopLevelAncestorType,
	findTagById,
	collectDescendantIds,
	collectAncestorIds,
	subtreeContainsId,
};
